<template>
  <div class="editor-container">
    <div v-if="selectedElement" class="card">
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" :mode="'Editor'" />
      <div class="card-body">

        <form @submit.prevent="saveElement">
          <div hidden class="mb-3">
            <label class="form-label">Element Label</label>
            <input type="text" class="form-control" v-model="elementLabel" @input="handleLabelChange(elementLabel)">
          </div>
          <div hidden class="mb-3">
            <label class="form-label">Element ID</label>
            <input type="text" class="form-control" v-model="selectedElement.el_ID" readonly>
          </div>
          <div hidden class="mb-3" v-show="selectedElement.isNewElement">
            <label class="form-label">Type</label>
            <input type="text" class="form-control" v-model="selectedElement.el_Type">
          </div>
          <div class="mb-3">
            <h3>
              <input placeholder="Main prompt" type="text" class="inputMainPrompt" v-model="elText" />

            </h3>
          </div>
          <div class="form-group mb-3">
            <div ref="quillEditor" class="quill-editor"></div>
          </div>

          <!-- Switch pour ajouter un texte de référence -->
          <div class="form-check form-switch mb-3">
            <input class="form-check-input" type="checkbox" role="switch" id="switchTextReference"
              v-model="hasTextReference" />
            <label class="form-check-label" for="switchTextReference">Add a Text reference</label>
          </div>

          <!-- Textarea pour le texte de référence -->
          <div v-if="hasTextReference" class="mb-3">
            <label class="form-label">Reference Text</label>
            <textarea 
              class="form-control reference-text" 
              v-model="refText" 
              placeholder="max: 12000 words" 
              rows="8"
              :maxlength="60000"
              @input="validateRefTextLength"
            ></textarea>
            <div class="d-flex justify-content-between mt-1">
              <small class="text-muted">
                Paste your reference lesson or definitions here (invisible in preview/render, used for AI correction)
              </small>
              <small :class="refTextLengthClass">
                {{ refTextWordCount }} / 12000 words ({{ refTextCharCount }} / 60000 characters)
              </small>
            </div>
          </div>

          <!-- <p>You can use both : AI or text match to correct the answer and define a score.</p> -->
          <div class="IAParams">AI Correction simulator can be implemented in Preview. Look at this demo - <a target="_blank" href="https://waspl.wiquid.fr/2025/05/08/ai-assistance-in-assessment-building-first-exploration-in-waspl/"> AI Assistance in assessment building</a>
          </div>
          <div class="form-check form-switch mb-3">
            <input class="form-check-input" type="checkbox" role="switch" id="switchCheckDefault"
              v-model="useGrid" />
            <label class="form-check-label" for="switchCheckDefault">Create a Correction Grid</label>
          </div>

          <div v-if="!useGrid" class="input-group mb-3 " style="width: 30%;">
            <span class="input-group-text">Max Score:</span>
            <input type="number" class="form-control NOAIMaxScore" v-model.number="noAIMaxScore" aria-label="Max Score"
              min="0" step="1" placeholder="Enter max score">
          </div>
          <div v-else class="input-group mb-3 " style="width: 30%;">
            <span class="input-group-text">Max Score:</span>
            <input readonly type="number" class="form-control NOAIMaxScore" v-model.number="noAIMaxScore" aria-label="Max Score"
              min="0" step="1" placeholder="Auto-calculated from grid">
            <span class="input-group-text bg-success text-white" title="Automatically calculated from correction axes">
              <font-awesome-icon icon="calculator" />
            </span>
          </div>

          <!-- Grid Configuration -->
          <div v-if="useGrid" class="AI-Preparation">
            <AICorrAxes :correctionAxes="selectedElement.correctionAxes || []"
              @update:correctionAxes="updateCorrectionAxes" />
          </div>
        </form>
      </div>
            
      <!-- Slider de difficulté -->
      <DifficultySlider 
        v-model="selectedElement.difficulty_rating"
        @update:model-value="updateElement('difficulty_rating', $event)"
      />
      
      <dimension v-if="hasDimensions" />
      <div class="card-footer ">
        <EditorActionBar :selectedElement="selectedElement" :elementLabel="elementLabel" @save-element="saveElement"
          :isItemBank />
      </div>
    </div>
    <div v-else class="alert alert-info">
      <p>Select an element to edit</p>
    </div>
  </div>
</template>

<script setup>
import cardHeader from '../default/cardHeader.vue';
import AICorrAxes from '../default/AICorrAxes.vue';
import { useTestStore } from '../../../stores/testStore'
import { computed, ref, watch, onMounted } from 'vue'
import EditorActionBar from '../default/EditorActionBar.vue';
import Quill from 'quill'
import dimension from '../default/dimension.vue';

import DifficultySlider from '../../common/DifficultySlider.vue';
// Définir les props Pour l'adaptation de l'action bar footer au contexte : TEST or ITEM BANK
const props = defineProps({
  isItemBank: {
    type: Boolean,
    default: false
  }
});

const useGrid = computed({
  get() {
    return selectedElement.value?.useGrid ?? false;
  },
  set(newVal) {
    if (!selectedElement.value) return;
    selectedElement.value.useGrid = newVal;
    
    // Si on active la grille
    if (newVal) {
      if (!selectedElement.value.correctionAxes || selectedElement.value.correctionAxes.length === 0) {
        // Initialiser avec un axe par défaut avec les bonnes valeurs
        selectedElement.value.correctionAxes = [{
          axis: '',
          point: 1,    // Valeur par défaut pour "if true"
          penalty: 0   // Valeur par défaut pour "if false"
        }];
      } else {
        // Corriger les valeurs existantes si elles ont les anciennes valeurs par défaut
        selectedElement.value.correctionAxes.forEach(axis => {
          // Si point est null ou 10 (ancienne valeur), le mettre à 1
          if (axis.point === null || axis.point === 10) {
            axis.point = 1;
          }
          // Si penalty est null ou 10 (ancienne valeur), le mettre à 0
          if (axis.penalty === null || axis.penalty === 10) {
            axis.penalty = 0;
          }
        });
      }
    }
    
    // Ajouter la vérification isItemBank ici aussi pour la cohérence
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
});

const store = useTestStore()

// Computed pour vérifier si les dimensions doivent être affichées
const hasDimensions = computed(() => {
  const selectedEl = store.selectedElement;
  const testData = store.testData;
  
  // Afficher si l'élément a déjà des dimensions assignées
  const hasAssignedDimensions = selectedEl?.dimension && selectedEl.dimension.length > 0;
  
  // Afficher si des dimensions sont disponibles pour être ajoutées
  const hasAvailableDimensions = testData?.dimensions && testData.dimensions.length > 0;
  
  return hasAssignedDimensions || hasAvailableDimensions;
});

const selectedElement = computed(() => store.selectedElement)
const elementLabel = ref('')
const correctionAxes = ref([])

const quillEditor = ref(null) // Référence pour l'éditeur Quill
let quillInstance = null // Instance de Quill

// Mettre à jour les axes de correction
const updateCorrectionAxes = (newAxes) => {
  if (props.isItemBank) {
    console.log("isItemBank is true. Skipping store.updateElement.");
    return;
  }

  if (selectedElement.value) {
    selectedElement.value.correctionAxes = newAxes;
    console.log('Correction axes updated:', newAxes);
    console.log('New calculated maxScore:', calculatedMaxScoreFromAxes.value);
    store.updateElement(selectedElement.value);
  }
};

watch(() => store.selectedElementLabel, (newLabel) => {
  elementLabel.value = newLabel || ''
}, { immediate: true })


// 🟢 **Ajout d'un computed property pour el_Text** 🟢
const elText = computed({
  get: () => selectedElement.value?.el_Text || '',
  set: (newValue) => {
    if (!selectedElement.value) return;

    selectedElement.value.el_Text = newValue;
    
    // Ajouter la vérification isItemBank ici aussi
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
});

// Computed property pour le switch du texte de référence
const hasTextReference = computed({
  get: () => selectedElement.value?.hasTextReference ?? false,
  set: (newValue) => {
    if (!selectedElement.value) return;
    
    selectedElement.value.hasTextReference = newValue;
    
    // Si on désactive le switch, nettoyer le texte de référence
    if (!newValue) {
      selectedElement.value.refText = '';
    }
    
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
});

// Computed property pour le texte de référence
const refText = computed({
  get: () => selectedElement.value?.refText || '',
  set: (newValue) => {
    if (!selectedElement.value) return;
    
    selectedElement.value.refText = newValue;
    
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
});

// Computed properties pour la validation de longueur
const refTextCharCount = computed(() => {
  return refText.value.length;
});

const refTextWordCount = computed(() => {
  if (!refText.value.trim()) return 0;
  return refText.value.trim().split(/\s+/).length;
});

const refTextLengthClass = computed(() => {
  const wordCount = refTextWordCount.value;
  const charCount = refTextCharCount.value;
  
  if (wordCount > 12000 || charCount > 60000) {
    return 'text-danger';
  } else if (wordCount > 10000 || charCount > 50000) {
    return 'text-warning';
  } else {
    return 'text-muted';
  }
});

// Fonction pour valider la longueur du texte de référence
const validateRefTextLength = () => {
  if (refTextCharCount.value > 60000) {
    // Tronquer le texte si trop long
    refText.value = refText.value.substring(0, 60000);
  }
  
  if (refTextWordCount.value > 12000) {
    console.warn('Reference text exceeds 12000 words limit');
  }
};

// 🟢 **Ajout d'un watch pour détecter un changement dans el_Text et mettre à jour le store** 🟢
// watch(() => selectedElement.value?.el_Text, (newText) => {
//   if (!selectedElement.value) return;

//   store.updateElement({
//     ...selectedElement.value,
//     el_Text: newText
//   });
// });

// Utility function pour mettre à jour les éléments (comme dans les autres éditeurs)
const updateElement = (key, value) => {
  if (!props.isItemBank) {
    // Sauvegarde automatique uniquement dans le contexte du test
    console.log(`Updating element in test context: key=${key}, value=${value}`);
    if (selectedElement.value) {
      const keys = key.split('.');
      let obj = selectedElement.value;
      while (keys.length > 1) obj = obj[keys.shift()];
      obj[keys[0]] = value;
      store.updateElement(selectedElement.value);
    }
  } else {
    // Logique spécifique à l'Item Bank (si nécessaire)
    console.log("Item Bank context detected. No auto-save triggered.");
  }
};

const handleLabelChange = (newLabel) => {

  if (props.isItemBank) {
    console.log("isItemBank is true. Skipping store.updateElement.");
    return;
  }
  store.updateElementLabel(newLabel)
}

// Watcher pour recharger le contenu Quill quand l'élément change
watch(
  () => selectedElement.value,
  (newElement) => {
    if (newElement && quillInstance) {
      // Recharger le contenu Quill
      if (newElement.el_RichText) {
        quillInstance.root.innerHTML = newElement.el_RichText;
      } else {
        quillInstance.root.innerHTML = '';
      }
    }
  }
);

const saveElement = () => {

  if (props.isItemBank) {
    console.log("isItemBank is true. Skipping store.updateElement.");
    return;
  }

  if (selectedElement.value) {
    store.updateElement(selectedElement.value);
  }
};

// 1. Computed property pour calculer le maxScore automatiquement depuis les axes de correction
const calculatedMaxScoreFromAxes = computed(() => {
  if (!selectedElement.value?.correctionAxes || !Array.isArray(selectedElement.value.correctionAxes)) {
    return 0;
  }
  
  // Sommer tous les scores "if true" (propriété point) de chaque axe
  return selectedElement.value.correctionAxes.reduce((total, axis) => {
    return total + (Number(axis.point) || 0);
  }, 0);
});

// 2. Computed property pour NOAIMaxScore avec calcul automatique en mode Grid
const noAIMaxScore = computed({
  get: () => {
    // Si le mode Grid est activé, utiliser le calcul automatique
    if (useGrid.value) {
      return calculatedMaxScoreFromAxes.value;
    }
    // Sinon, utiliser la valeur manuelle
    return selectedElement.value?.noAIMaxScore || 0;
  },
  set: (newValue) => {
    // En mode Grid, empêcher la modification manuelle
    if (useGrid.value) {
      console.log('Cannot manually set maxScore in Grid mode - automatically calculated');
      return;
    }
    
    selectedElement.value.maxScore = newValue;
    if (!selectedElement.value) return;

    selectedElement.value.noAIMaxScore = Number(newValue) || 0;

    if (!props.isItemBank) {
      console.log('Updating noAIMaxScore to:', newValue);
      store.updateElement(selectedElement.value);
    }
  }
});

// 3. Watcher pour synchroniser automatiquement le maxScore calculé avec l'élément
watch(
  () => calculatedMaxScoreFromAxes.value,
  (newCalculatedScore) => {
    if (useGrid.value && selectedElement.value) {
      console.log('Auto-updating maxScore from correction axes:', newCalculatedScore);
      selectedElement.value.maxScore = newCalculatedScore;
      selectedElement.value.noAIMaxScore = newCalculatedScore;
      
      if (!props.isItemBank) {
        store.updateElement(selectedElement.value);
      }
    }
  },
  { immediate: true }
);

// 4. Watcher pour détecter les changements manuels et sauvegarder (mode non-Grid)
watch(
  () => selectedElement.value?.noAIMaxScore,
  (newScore, oldScore) => {
    // Ignorer les changements en mode Grid car ils sont gérés automatiquement
    if (useGrid.value) return;
    
    console.log('noAIMaxScore changed from:', oldScore, 'to:', newScore);
    selectedElement.maxScore = newScore; // Attention NO AI !

    if (selectedElement.value && !props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
);

// 3. Handler pour l'input (alternative au computed)
const handleNOAIMaxScoreChange = (event) => {
  const newValue = Number(event.target.value) || 0;

  if (selectedElement.value) {
    selectedElement.value.noAIMaxScore = newValue;

    if (!props.isItemBank) {
      console.log('Saving noAIMaxScore:', newValue);
      store.updateElement(selectedElement.value);
    }
  }
};

// 4. Watcher pour initialiser la valeur au chargement d'un élément
watch(
  () => selectedElement.value,
  (newElement) => {
    if (newElement) {
      // Initialiser noAIMaxScore si pas défini
      if (typeof newElement.noAIMaxScore === 'undefined') {
        newElement.noAIMaxScore = 1;
      }

      // Initialiser hasTextReference si pas défini
      if (typeof newElement.hasTextReference === 'undefined') {
        newElement.hasTextReference = false;
      }

      // Initialiser refText si pas défini
      if (typeof newElement.refText === 'undefined') {
        newElement.refText = '';
      }

      if (!props.isItemBank) {
        console.log('Initializing shortAnswer element properties');
        store.updateElement(newElement);
      }
    }
  },
  { immediate: true }
);


const debugNOAIMaxScore = () => {
  console.log('=== DEBUG NOAIMaxScore ===');
  console.log('selectedElement:', selectedElement.value);
  console.log('selectedElement.noAIMaxScore:', selectedElement.value?.noAIMaxScore);
  console.log('noAIMaxScore ref/computed:', noAIMaxScore.value);
  console.log('useGrid:', useGrid.value);
  console.log('isItemBank:', props.isItemBank);
  console.log('========================');
};

// Ajouter un watcher de debug temporaire
watch(
  () => ({
    elementScore: selectedElement.value?.noAIMaxScore,
    localScore: noAIMaxScore.value,
    useGrid: useGrid.value
  }),
  (newVal, oldVal) => {
    console.log('Debug watcher - Values changed:', { newVal, oldVal });
  }
);





// Initialisation de l'éditeur Quill
onMounted(() => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: [
          // Styles de texte
          ['bold', 'italic', 'underline', 'strike'], // Gras, italique, souligné, barré

          // Titres
          [{ header: 1 }, { header: 2 }], // Titres niveau 1 et 2
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // Menu déroulant des tailles de titre

          // Listes et indentations
          [{ list: 'ordered' }, { list: 'bullet' }], // Listes ordonnées et à puces
          [{ indent: '-1' }, { indent: '+1' }], // Indentation

          // Alignement
          [{ align: [] }], // Alignement : gauche, centre, droite, justifié

          // Couleurs
          [{ color: [] }, { background: [] }], // Couleur de texte et d'arrière-plan

          // Médias
          ['image', 'link'], // Insérer une image, un lien, ou une vidéo

          // Nettoyer la mise en forme
          ['clean'], // Bouton pour effacer le formatage
        ],
      },
    })

    // Synchroniser Quill avec les données
    if (selectedElement.value?.el_RichText) {

      quillInstance.root.innerHTML = selectedElement.value.el_RichText
    }

    quillInstance.on('text-change', () => {
      if (selectedElement.value) {
        selectedElement.value.el_RichText = quillInstance.root.innerHTML;

        if (!props.isItemBank) {
          store.updateElement(selectedElement.value);
        }
      }
    })


  }
})
</script>

<style scoped>
.card-header {
  background-color: #426C9C;
  color: white
}

.sendToItemLibBT {
  margin-left: 10px;
}

.IAParams {
  font-size: 0.8rem;
  color: grey;
  padding: 5px;
  font-style: italic;
  margin-bottom: 10px;
}

/* .shortAnswerInput {
  width: 100%;
  
} */

.reference-text {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 200px;
}

.reference-text:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}
</style>